import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ShieldCheck } from 'lucide-react';
import { authorizationsApi } from '../lib/api-client';
import { cn } from '../lib/cn';
import { Button } from './ui/button';
import { Input, Label, FieldHint } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ConfirmDialog } from './ui/confirm-dialog';
import { EmptyState, friendlyErrorMessage } from './ui/states';
import { WhatsThis } from './ui/tooltip';
import { useToast } from './ui/toast';
import { can } from '../lib/rbac';
import { useAuth } from '../lib/auth-context';
import type { AuthorizationMethod } from '../lib/types';

const METHOD_LABEL: Record<AuthorizationMethod, string> = {
  signed_document: 'Signed document',
  dns_txt: 'DNS TXT record',
  file_token: 'File on the web server',
  email_domain: 'Verified email domain',
  account_owner: 'Cloud account ownership',
};

const METHOD_HINT: Record<AuthorizationMethod, string> = {
  signed_document: 'Upload a signed authorization letter as evidence (tracked by reference).',
  dns_txt: "We'll ask you to publish a DNS TXT record proving you control the domain.",
  file_token: "We'll ask you to place a file at a known path on the web server.",
  email_domain: 'Verified based on your account email domain.',
  account_owner: 'Verified based on cloud account ownership.',
};

function CreateAuthorizationForm({ engagementId, onDone }: { engagementId: string; onDone: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [method, setMethod] = useState<AuthorizationMethod>('dns_txt');
  const [authorizedByName, setAuthorizedByName] = useState('');
  const [authorizedByEmail, setAuthorizedByEmail] = useState('');
  const [notBefore, setNotBefore] = useState(() => new Date().toISOString().slice(0, 10));
  const [notAfter, setNotAfter] = useState(() => new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      authorizationsApi.create(engagementId, {
        method,
        notBefore: new Date(notBefore).toISOString(),
        notAfter: new Date(notAfter).toISOString(),
        authorizedBy: { name: authorizedByName, email: authorizedByEmail },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['authorizations', engagementId] });
      toast({ title: 'Authorization created', description: "Next: prove it's legitimate.", variant: 'success' });
      onDone();
    },
    onError: (err) => setError(friendlyErrorMessage(err)),
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        mutation.mutate();
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="auth-method">
          How will you prove authorization? <WhatsThis>{METHOD_HINT[method]}</WhatsThis>
        </Label>
        <Select value={method} onValueChange={(v) => setMethod(v as AuthorizationMethod)}>
          <SelectTrigger id="auth-method">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.entries(METHOD_LABEL) as Array<[AuthorizationMethod, string]>).map(([v, label]) => (
              <SelectItem key={v} value={v}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="auth-name">Authorized by (name)</Label>
          <Input id="auth-name" value={authorizedByName} onChange={(e) => setAuthorizedByName(e.target.value)} required />
        </div>
        <div className="space-y-1">
          <Label htmlFor="auth-email">Authorized by (email)</Label>
          <Input id="auth-email" type="email" value={authorizedByEmail} onChange={(e) => setAuthorizedByEmail(e.target.value)} required />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="auth-start">Valid from</Label>
          <Input id="auth-start" type="date" value={notBefore} onChange={(e) => setNotBefore(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="auth-end">Valid until</Label>
          <Input id="auth-end" type="date" value={notAfter} onChange={(e) => setNotAfter(e.target.value)} />
        </div>
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <Button type="submit" className="w-full" disabled={mutation.isPending}>
        Create authorization
      </Button>
    </form>
  );
}

function SubmitProofForm({ authorizationId, method, onDone }: { authorizationId: string; method: AuthorizationMethod; onDone: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState('');
  const [token, setToken] = useState('');
  const [evidenceRef, setEvidenceRef] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      method === 'dns_txt' || method === 'file_token'
        ? authorizationsApi.submitProof(authorizationId, { method, target, token })
        : authorizationsApi.submitProof(authorizationId, { method, evidenceRef }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['authorizations'] });
      toast({ title: 'Ownership verified', variant: 'success' });
      onDone();
    },
    onError: (err) => setError(friendlyErrorMessage(err)),
  });

  const isDnsOrFile = method === 'dns_txt' || method === 'file_token';

  return (
    <form
      className="space-y-3 rounded-xl border border-border p-4"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        mutation.mutate();
      }}
    >
      <p className="text-sm font-medium text-ink">Prove authorization: {METHOD_LABEL[method]}</p>
      {isDnsOrFile ? (
        <>
          <div className="space-y-1">
            <Label htmlFor="proof-target">{method === 'dns_txt' ? 'Domain' : 'Base URL'}</Label>
            <Input id="proof-target" value={target} onChange={(e) => setTarget(e.target.value)} placeholder={method === 'dns_txt' ? 'example.com' : 'https://example.com'} />
            <FieldHint>
              {method === 'dns_txt'
                ? 'Publish a TXT record at _defendiq-verify.<domain> with the token below.'
                : 'Place a file at /.well-known/defendiq-verify.txt containing the token below.'}
            </FieldHint>
          </div>
          <div className="space-y-1">
            <Label htmlFor="proof-token">Token</Label>
            <Input id="proof-token" value={token} onChange={(e) => setToken(e.target.value)} placeholder="the value you published" />
          </div>
        </>
      ) : (
        <div className="space-y-1">
          <Label htmlFor="evidence-ref">Evidence reference</Label>
          <Input id="evidence-ref" value={evidenceRef} onChange={(e) => setEvidenceRef(e.target.value)} placeholder="doc:123 or a stored reference" />
        </div>
      )}
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <Button type="submit" size="sm" disabled={mutation.isPending}>
        Verify
      </Button>
    </form>
  );
}

export function AuthorizationPanel({ engagementId }: { engagementId: string }) {
  const { principal } = useAuth();
  const canWrite = principal?.role === undefined || can(principal.role, 'authorization:write');
  const canActivate = principal?.role === undefined || can(principal.role, 'authorization:activate');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const query = useQuery({ queryKey: ['authorizations', engagementId], queryFn: () => authorizationsApi.list(engagementId) });
  const activateMutation = useMutation({
    mutationFn: (id: string) => authorizationsApi.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['authorizations', engagementId] });
      toast({ title: 'Authorization activated — testing can now begin', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not activate', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  if (query.isLoading) return null;
  const authorizations = query.data?.authorizations ?? [];
  const latest = authorizations[authorizations.length - 1];

  if (!latest) {
    if (!canWrite) {
      return <EmptyState title="No authorization yet" description="An admin or operator needs to authorize this engagement before it can be tested." />;
    }
    if (creating) {
      return <CreateAuthorizationForm engagementId={engagementId} onDone={() => setCreating(false)} />;
    }
    return (
      <EmptyState
        icon={<ShieldCheck size={32} />}
        title="This engagement isn't authorized yet"
        description="Nothing can be tested until you record explicit, time-boxed authorization from the asset owner."
        action={<Button onClick={() => setCreating(true)}>Authorize this engagement</Button>}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 rounded-xl border border-border p-4">
        <div className={cn('rounded-full p-2', latest.status === 'active' ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning')}>
          {latest.status === 'active' ? <CheckCircle2 size={20} /> : <ShieldCheck size={20} />}
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium capitalize text-ink">{latest.status}</p>
          <p className="text-xs text-ink-muted">
            {METHOD_LABEL[latest.method]} · authorized by {latest.authorizedBy.name} · valid{' '}
            {new Date(latest.notBefore).toLocaleDateString()}–{new Date(latest.notAfter).toLocaleDateString()}
          </p>
        </div>
        {latest.status === 'verified' && canActivate ? (
          <ConfirmDialog
            trigger={<Button size="sm">Activate</Button>}
            title="Activate this authorization?"
            description="Once active, testing can begin against this engagement's approved scope. Only activate once you're certain the authorization is genuine."
            confirmLabel="Activate"
            onConfirm={() => activateMutation.mutateAsync(latest.id)}
          />
        ) : null}
      </div>
      {latest.status === 'pending' && canWrite ? (
        <SubmitProofForm authorizationId={latest.id} method={latest.method} onDone={() => queryClient.invalidateQueries({ queryKey: ['authorizations', engagementId] })} />
      ) : null}
    </div>
  );
}
